/**
 * InvoiceCard — invoice metadata, line items, totals, and Pay Now CTA.
 */

import type { PublicInvoiceView } from '@aelita/shared';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LineItemTable } from './LineItemTable';
import { formatAmount, formatAmountStr } from './formatAmount';

interface InvoiceCardProps {
  invoice: PublicInvoiceView;
  onPayNow: () => void;
  isCheckingOut: boolean;
  checkoutError: string | null;
}

const fmtDate = (d: Date | string) =>
  (d instanceof Date ? d : new Date(d)).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

/**
 * Validates that a value is a 2dp decimal string (e.g. "2691.67").
 * Returns true only when the value is present and parseable to a finite number.
 * This guard exists to prevent $NaN from reaching the "Amount due" line if the
 * API ever returns a payload without balanceDue (stale cache, schema drift, etc.).
 */
function isValidAmountStr(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const n = parseFloat(value);
  return isFinite(n) && n >= 0;
}

export function InvoiceCard({ invoice, onPayNow, isCheckingOut, checkoutError }: InvoiceCardProps) {
  // Guard: balanceDue must be a valid numeric string before we do any arithmetic
  // or render the Pay Now button. If it's absent (API outage, stale schema,
  // container cache) we show an error and suppress the checkout path entirely.
  // Failing loudly here is correct — a wrong charge is worse than a clear error.
  const balanceDueValid = isValidAmountStr(invoice.balanceDue);

  // amountPaid is a 2dp string — parse once for the conditional check.
  const amountPaidValue = isValidAmountStr(invoice.amountPaid) ? parseFloat(invoice.amountPaid) : 0;
  const hasPartialPayment = amountPaidValue > 0;

  return (
    <Card>
      <CardBody className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Invoice #{invoice.invoiceNumber}</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-0.5">{invoice.clientName}</p>
          <p className="text-sm text-gray-400 mt-1">Due {fmtDate(invoice.dueAt)}</p>
        </div>

        <hr className="border-gray-100 dark:border-gray-800" />
        <LineItemTable lineItems={invoice.lineItems} />

        {/* Totals block */}
        <div className="space-y-1.5 pt-3 border-t border-gray-100 dark:border-gray-800">
          {/* Subtotal */}
          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300">
            <span>Subtotal</span>
            <span className="tabular-nums">{formatAmount(invoice.subtotal)}</span>
          </div>

          {/* Amount paid — only shown when there's a prior payment */}
          {hasPartialPayment && (
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300">
              <span>Amount paid</span>
              <span className="tabular-nums">−{formatAmountStr(invoice.amountPaid)}</span>
            </div>
          )}

          {/* Divider + Amount due */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
            {balanceDueValid ? (
              <div className="flex justify-between items-baseline">
                <span className="text-base font-semibold text-gray-900 dark:text-gray-100">Amount due</span>
                <span className="text-2xl font-bold text-primary-600 dark:text-primary-400 tabular-nums">
                  {formatAmountStr(invoice.balanceDue)}
                </span>
              </div>
            ) : (
              <div
                role="alert"
                data-testid="balance-due-error"
                className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300"
              >
                Unable to determine the amount due. Please contact{' '}
                <a href="mailto:billing@lyokotech.com" className="underline font-medium">
                  billing@lyokotech.com
                </a>{' '}
                before proceeding.
              </div>
            )}
          </div>
        </div>

        {checkoutError && (
          <div
            role="alert"
            className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300"
          >
            {checkoutError}
          </div>
        )}

        {/* Pay Now is suppressed when balanceDue is not a valid amount.
            A wrong charge is worse than a missing button. */}
        {balanceDueValid && (
          <Button
            className="w-full"
            size="lg"
            onClick={onPayNow}
            isLoading={isCheckingOut}
            disabled={isCheckingOut}
            aria-label={`Pay ${formatAmountStr(invoice.balanceDue)} now`}
          >
            Pay Now
          </Button>
        )}
      </CardBody>
    </Card>
  );
}
